import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { visuallyHiddenStyle } from '@/components/common/utils';
import { bodyMedium } from '@/styles/typography';

import type { MouseEvent } from 'react';

export interface FilterOptionProps {
  value: string;
  name: string;
  isChecked: boolean;
  onClick: (e: MouseEvent<HTMLInputElement>) => void;
}

const FilterOption = ({
  value,
  name,
  isChecked,
  onClick,
}: FilterOptionProps) => {
  return (
    <FilterOptionWrapper isChecked={isChecked}>
      <OptionInput
        type="radio"
        value={value}
        id={`filter-option-${value}`}
        name={name}
        onClick={onClick}
        aria-checked={isChecked}
      />
      <label
        aria-label={`filter by ${value}`}
        htmlFor={`filter-option-${value}`}
      >
        <HiddenSpan>{value === 'All' ? 'Show ' : 'Filter by '}</HiddenSpan>
        {value}
      </label>
    </FilterOptionWrapper>
  );
};

export interface FiltersGroupProps {
  label: string;
  groupName: string;
  options: string[];
  onChange: (e: MouseEvent<HTMLInputElement>) => void;
  filterBy: string;
}

export const Filters = ({
  options,
  label,
  groupName,
  onChange,
  filterBy,
}: FiltersGroupProps) => {
  return (
    <FiltersContainer role="radiogroup" aria-labelledby="filter-title">
      <HiddenSpan id="filter-title">{label}</HiddenSpan>
      <FilterOption
        value="All"
        isChecked={filterBy === 'All'}
        key="All"
        name={groupName}
        onClick={onChange}
      />
      {options.map((value) => {
        return (
          <FilterOption
            value={value}
            isChecked={filterBy === value}
            key={value}
            name={groupName}
            onClick={onChange}
          />
        );
      })}
    </FiltersContainer>
  );
};

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-x-small);

  ${screen.md} {
    gap: var(--spacing-x-large);
  }
`;

const OptionInput = styled.input`
  display: inline;
`;

const HiddenSpan = styled.span`
  ${visuallyHiddenStyle}
`;

const FilterOptionWrapper = styled.div<{ isChecked: boolean }>`
  margin: 0;

  border: 0;
  background: transparent;
  padding: 0;
  color: inherit;
  position: relative;
  display: inline-flex;
  flex-direction: column;
  justify-content: space-between;

  label {
    cursor: pointer;
    ${bodyMedium}
    font-weight: ${({ isChecked }) =>
      isChecked ? 'var(--font-weight-medium)' : 'var(--font-weight-normal)'};
    background-color: ${({ isChecked }) =>
      isChecked ? 'var(--decorative-sunbeam)' : 'var(--transparent)'};
    padding: var(--spacing-50) var(--spacing-100);
    border-radius: var(--radius-xs);
    white-space: nowrap;
  }

  span {
    transition: all 0.2s ease-in;
  }

  input[type='radio']:focus-visible ~ label {
    outline: 2px solid var(--border-action);
    outline-offset: 2px;
  }

  input[type='radio'] {
    appearance: none;
    margin: 0;
    opacity: 0;
  }

  /* hack to prevent layout shift when selecting filter */
  &::before {
    content: attr(data-text);
    display: block;
    height: 0;
    visibility: hidden;
    overflow: hidden;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    pointer-events: none;
    font-weight: var(--font-weight-medium);

    @media speech {
      display: none;
    }
  }
`;
